import React from "react";
import { useMutation } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Pencil, Trash2, Check } from "lucide-react";

export const CatalogRow = ({ r, vendors, onEdit, onChanged, canEdit }) => {
  const opts = { onSuccess: onChanged, onError: (e) => toast.error(apiErr(e)) };
  const invite = useMutation({ mutationFn: async (ids) => (await api.put("/procurement/bidding/invite", { item_id: r.id, vendor_ids: ids })).data, ...opts });
  const needs = useMutation({ mutationFn: async (v) => (await api.post("/procurement/bidding/needs-offer", { item_id: r.id, needs_offer: v })).data, ...opts });
  const pref = useMutation({ mutationFn: async (v) => (await api.post("/procurement/bidding/preferred", { item_id: r.id, vendor_id: v })).data, ...opts });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/recipes/ingredients/${r.id}`)).data,
    onSuccess: () => { toast.success("Το είδος διαγράφηκε"); onChanged(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const offer = new Set(r.offer_vendor_ids || []);
  const toggle = (id) => {
    const next = new Set(offer);
    next.has(id) ? next.delete(id) : next.add(id);
    invite.mutate([...next]);
  };
  const wants = r.needs_offer !== false;
  return (
    <tr className="border-t border-[#EFEBE3] hover:bg-[#FBFAF7]" data-testid={`catalog-row-${r.id}`}>
      <td className="px-4 py-2 font-medium text-[#45423C]">{r.name}{r.supplier && <div className="text-[11px] text-[#B9AE9A] font-normal">{r.supplier}</div>}</td>
      <td className="px-3 py-2 text-[#6F6A60]">{r.category || "—"}</td>
      <td className="px-3 py-2 text-[#6F6A60]">{r.unit}</td>
      <td className="px-3 py-2 text-right tabular">{fmtEuroCents(r.price_per_unit)}</td>
      <td className="px-3 py-2 text-center">
        <button data-testid={`catalog-needs-${r.id}`} onClick={() => needs.mutate(!wants)} title={wants ? "Θέλω προσφορά" : "Χωρίς προσφορά"}
          className={`h-6 w-6 rounded-md border inline-flex items-center justify-center transition-colors ${wants ? "bg-[#6E7A63] border-[#6E7A63] text-white" : "bg-white border-[#D8D0C2]"}`}>
          {wants && <Check className="h-4 w-4" />}
        </button>
      </td>
      <td className="px-3 py-2">
        <div className={`flex flex-wrap gap-1.5 ${wants ? "" : "opacity-60"}`}>
          {vendors.map((v) => {
            const on = offer.has(v.id);
            return (
              <button key={v.id} data-testid={`catalog-offer-${r.id}-${v.id}`} onClick={() => toggle(v.id)} disabled={invite.isPending} title={v.name}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] max-w-[160px] truncate transition-colors ${on ? "bg-[#C86235] border-[#C86235] text-white" : "bg-white border-[#E3DED4] text-[#6F6A60] hover:border-[#C86235]"}`}>
                {on ? "✓ " : ""}{v.name}
              </button>
            );
          })}
          {vendors.length === 0 && <span className="text-xs text-[#B9AE9A]">Προσθέστε προμηθευτές</span>}
        </div>
      </td>
      <td className="px-3 py-2">
        <select data-testid={`catalog-pref-${r.id}`} value={r.preferred_vendor_id || ""} onChange={(e) => pref.mutate(e.target.value)}
          className="rounded-lg border border-[#E3DED4] px-2 py-1 text-xs max-w-[170px]">
          <option value="">—</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        {canEdit && <>
        <button data-testid={`catalog-edit-${r.id}`} onClick={onEdit} className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="Επεξεργασία"><Pencil className="h-4 w-4" /></button>
        <button data-testid={`catalog-del-${r.id}`} onClick={() => window.confirm(`Διαγραφή «${r.name}»;`) && del.mutate()} className="p-1.5 rounded-lg text-[#991B1B] hover:bg-[#FEF2F2]" title="Διαγραφή"><Trash2 className="h-4 w-4" /></button>
        </>}
      </td>
    </tr>
  );
};
